import { ThreadChannel } from '@@types/Channel';
import { CHANNEL_ID, LOG_ID, MEMBER_ID, REACTION_ID, StringTMap } from '@@types/common';
import Log, { AllLog, DummyLog, InviteMemberLog } from '@@types/Log';
import { Reaction } from '@@types/Log/Reaction';
import LogFile from '@@types/LogFile';

import { LOG_FLAGS, MAIN_THREAD_CHANNEL_ID } from '~/constants';
import { animationEffectStore } from '~/stores/AnimationEffectStore';
import { channelsStore } from '~/stores/ChannelsStore';
import { channelLogData } from '~/stores/data/ChannelLogData';
import { mainDetailUiState } from '~/stores/DetailUiState';
import { uiState } from '~/stores/UiState';
import {
  canOverlap,
  getPreviewLog,
  getRenderableFiles,
  isCommandLog,
  isEphemeralLog,
  isNonDummyLog,
  isNonEphemeralLog,
  isNonInviteMemberLog,
} from '~/utils/channelLogsUtil';
import { isInTimeUnit } from '~/utils/dateUtil';
import { lift, mMaybe } from '~/utils/monadUtil';
import { isEmpty } from 'lodash';
import { action, comparer, computed, observable, reaction, runInAction } from 'mobx';
import {
  allPass,
  ascend,
  curry,
  descend,
  equals,
  find,
  findLast,
  identity,
  invertObj,
  isNil,
  map,
  merge,
  pipe,
  prop,
  propSatisfies,
  reject,
  sequence,
  sortWith,
} from 'ramda';
import { Maybe } from 'ramda-fantasy';

class ChannelLogsStore {
  @observable
  public newLogId: LOG_ID | null = null;

  // 여기까지 읽음 처리를 위해 필요
  @observable.shallow
  public lastReadLogSeq: StringTMap<number> = {};

  @action
  public setLastReadLogSeq = curry((channelId: CHANNEL_ID, seq: number): void => {
    this.lastReadLogSeq = uiState.pickAndSetLruChannelIds(channelId, seq)(this.lastReadLogSeq);
  });

  // 현재 Log / dummy / invite
  @observable.shallow
  public isFetchingEdgeLogs: { prev: boolean; next: boolean } = {
    prev: false,
    next: false,
  };

  @observable
  public isLoadingLogs = false;

  @observable
  public notInWorkLogId: LOG_ID | null = null;

  public notInWorkChannelId: CHANNEL_ID | null = null;
  @observable isFetchingLogs = false;
  protected readonly getInner: (id: LOG_ID) => AllLog;
  protected readonly gc: () => void;
  @observable.shallow
  protected logIdMap: StringTMap<true> = {};

  protected dummyLogIdTokenMap: StringTMap<string> = {};
  private inviteLogIdMap: StringTMap<true> = {};
  // referenceLog
  @observable.shallow
  protected referenceLogIdMap: StringTMap<true> = {};

  @observable.shallow
  private showUserMap: StringTMap<boolean> = {};

  @observable.shallow
  private showSentAtMap: StringTMap<boolean> = {};

  @observable.shallow
  private showDayDividerMap: StringTMap<boolean> = {};

  @observable.shallow
  protected reactionMap: StringTMap<Reaction> = {};

  @observable
  private prevLoaded: CHANNEL_ID = null;

  @observable
  public isFirstLoaded = false;

  constructor() {
    const { getInStore, gc } = channelLogData.initInStore(() => this.usedLogIdMap);
    this.getInner = getInStore;
    this.gc = gc;

    reaction(
      () => this.logList,
      (logList) => {
        this.setVisibleProperty(logList);
      },
      { fireImmediately: true }
    );
  }

  @computed({ equals: comparer.shallow })
  public get logList(): AllLog[] {
    return sortWith<any>(
      [ascend(prop('seq')), descend(({ _dummy }) => (_dummy ? 0 : 1)), ascend(prop('sentAt'))],
      this.logDetailList
    );
  }

  @computed({ equals: comparer.shallow })
  public get imageFileLogList(): LogFile[] {
    return this.filterImageFileLogList(this.logList);
  }

  public filterImageFileLogList(logList: Log[]): LogFile[] {
    return pipe<any, any, any, any>(map(getPreviewLog), sequence(Maybe.of), mMaybe([], getRenderableFiles))(logList);
  }

  @computed
  get channel() {
    return channelsStore.getById(mainDetailUiState.contentsChannelId);
  }

  @action
  public setPrevLoaded = (channelId: CHANNEL_ID | null) => {
    this.prevLoaded = channelId;
  };

  @computed
  get isFirstLogLoaded() {
    if (this.isLoadingLogs) {
      return false;
    }
    return this.prevLoaded === this.channel?.channelId;
  }

  @computed
  get lastLog() {
    return findLast(allPass([isNonDummyLog, isNonEphemeralLog, isNonInviteMemberLog]), this.logList) as Log;
  }

  @computed
  get isLastLogLoaded() {
    if (this.isLoadingLogs) {
      return false;
    }

    return this.lastLog ? this.lastLog.seq >= (this.channel?.seq ?? -1) : true;
  }

  public get dummyLogTokenIdMap(): StringTMap<string> {
    return invertObj(this.dummyLogIdTokenMap);
  }

  @computed
  get newLog(): AllLog | null {
    return this.getInner(this.newLogId) || null;
  }

  @computed
  private get logDetailList(): AllLog[] {
    return Object.keys(this.logIdMap).map(this.getInner);
  }

  public getById = (logId: string) => {
    return this.usedLogIdMap[logId] ? this.getInner(logId) : null;
  };

  public getMainLogById = (logId: LOG_ID) => {
    return this.logIdMap[logId] ? this.getInner(logId) : null;
  };

  @action
  public mergeLogs = (logs: AllLog[] = []) => {
    const logIdList = logs.map((log) => {
      this.logIdMap[log.id] = true;
      return log.id;
    });

    channelLogData.setDataMulti(logIdList, logs);
  };

  public mergeDummyLogs = (logs: DummyLog[] = []) => {
    logs.forEach((log) => {
      this.dummyLogIdTokenMap[log.id] = log.token;
    });
    this.mergeLogs(logs);
  };

  public mergeInviteLogs = (logs: InviteMemberLog[] = []) => {
    logs.forEach((log) => {
      this.inviteLogIdMap[log.id] = true;
    });
    this.mergeLogs(logs);
  };

  @action
  public setLogs = (logs: Log[]) => {
    const logIdMap = {};
    const logMap = logs.reduce((acc, log) => {
      acc[log.id] = log;
      logIdMap[log.id] = true;
      return acc;
    }, {});

    channelLogData.setDataMap(logMap);
    this.setLogIdMap(logIdMap);
    this.dummyLogIdTokenMap = {};
    this.inviteLogIdMap = {};
    this.gc();
  };

  @action
  public setLog = (log: AllLog) => {
    channelLogData.setData(log.id, log);
    this.logIdMap[log.id] = true;
  };

  // referenceLog도 같이 update
  @action
  public updateLog = (log: Partial<AllLog>) => {
    const target = this.getInner(log.id);
    if (target) {
      this.setLog({ ...target, ...log });
    }
  };

  public appendDummyLog = (log: DummyLog) => {
    this.dummyLogIdTokenMap[log.id] = log.token;
    this.setLog(log);
  };

  public removeAllDummyLogs = () => {
    Object.keys(this.dummyLogIdTokenMap).forEach((id) => this.removeLogInLogMapById(id));
  };

  @action
  public clearDetailData = () => {
    this.setLogIdMap({});
    if (mainDetailUiState.contentsChannelId !== MAIN_THREAD_CHANNEL_ID) {
      this.dummyLogIdTokenMap = {};
      this.inviteLogIdMap = {};
      this.referenceLogIdMap = {};
    }
    animationEffectStore.setShow(false);
    this.gc();
  };

  public getReferenceLog = (logId: LOG_ID) => {
    return this.referenceLogIdMap[logId] && this.getInner(logId);
  };

  public safeGetOriginalLogById = (logId: string): Maybe<Log> => {
    if (isEmpty(logId)) return Maybe.Nothing();

    const log = this.getById(logId) || this.getReferenceLog(logId);
    return lift(identity, log) as Maybe<Log>;
  };

  @action
  public mergeReferenceLogMap = (logMap: StringTMap<Log> = {}) => {
    Object.keys(logMap).forEach((logId) => {
      this.referenceLogIdMap[logId] = true;
      channelLogData.setData(logId, Object.assign({}, this.getById(logId), logMap[logId]));
    });
  };

  @action
  public setReferenceLogMap = (logMap: StringTMap<Log> = {}): void => {
    this.referenceLogIdMap = {};
    this.mergeReferenceLogMap(logMap);
    this.gc();
  };

  @action
  public removeLog = (logId: LOG_ID) => {
    const log = this.getMainLogById(logId);
    if (log && isCommandLog(log) && isEphemeralLog(log)) {
      const removeLog = find(
        (_log: AllLog) => 'cmdToken' in _log && _log.cmdToken === log.cmdToken,
        this.logDetailList
      );
      if (removeLog) {
        this.removeLogInLogMapById(removeLog.id);
      }
    } else {
      this.removeLogInLogMapById(logId);
      this.removeLogInReferenceMap(logId);
    }
    this.removeInviteLogs(logId);
  };

  @action
  public removeInviteLogs = (masterLogId: LOG_ID, memberId?: MEMBER_ID) => {
    const allInviteLogList = Object.keys(this.inviteLogIdMap).map(this.getInner) as InviteMemberLog[];

    let inviteLogs = allInviteLogList.filter(propSatisfies(equals(masterLogId), 'masterLogId'));

    if (memberId) {
      inviteLogs = inviteLogs.filter(propSatisfies(equals(memberId), 'memberId'));
    }
    inviteLogs.forEach((log) => {
      this.removeLogInLogMapById(log.id);
    });
  };

  // LogActions setNewLog 를 통해서만 trigger
  @action
  public setNewLog = (log: Log | null) => {
    if (!log) {
      this.newLogId = null;
      return;
    }
    this.newLogId = log.id;
    channelLogData.setData(log.id, log);
  };

  @action
  public setIsFirstLoaded = (value: boolean) => {
    this.isFirstLoaded = value;
  };

  public getShowUserLog = (logId: LOG_ID) => {
    return this.showUserMap[logId];
  };

  public getShowSentAt = (logId: LOG_ID) => {
    return this.showSentAtMap[logId];
  };

  public getShowDayDivider = (logId: LOG_ID) => {
    return this.showDayDividerMap[logId];
  };

  public getReaction = (reactionId: REACTION_ID): Reaction => {
    return this.reactionMap[reactionId];
  };

  public setReaction = (reactionId: REACTION_ID, reaction: Reaction) => {
    this.reactionMap[reactionId] = reaction;
  };

  public removeReaction = (reactionId: REACTION_ID) => {
    delete this.reactionMap[reactionId];
  };

  public mergeReactionMap = (reactionMap: StringTMap<Reaction> = {}): void => {
    this.reactionMap = { ...this.reactionMap, ...reactionMap };
  };

  @action
  public clearLastReadLogSeq = (channelId) => {
    delete this.lastReadLogSeq[channelId];
  };

  @action
  public setIsFetchingEdgeLogs: (params: { next?: boolean; prev?: boolean }) => void = (params) => {
    this.isFetchingEdgeLogs = merge(this.isFetchingEdgeLogs, reject(isNil, params));
  };

  @action
  public setIsLoadingLogs = (value) => {
    this.isLoadingLogs = value;
  };

  @action
  public setNotInWorkLogId = (logId: LOG_ID | null, channelId?: CHANNEL_ID) => {
    if (this.notInWorkLogId !== logId) {
      this.notInWorkLogId = logId;
      this.notInWorkChannelId = logId && channelId;
    }
  };

  @action
  setIsFetchingLogs = (isFetchingLogs) => {
    this.isFetchingLogs = isFetchingLogs;
  };

  @computed({ equals: comparer.shallow, keepAlive: true })
  get usedLogIdMap() {
    return Object.assign(
      this.newLogId ? { [this.newLogId]: true } : {},
      this.logIdMap,
      this.dummyLogIdTokenMap,
      this.referenceLogIdMap
    );
  }

  @action
  private setLogIdMap = (idMap: StringTMap<true>) => {
    this.logIdMap = idMap;
  };

  @action
  private removeLogInLogMapById = (logId: string) => {
    delete this.logIdMap[logId];
    delete this.dummyLogIdTokenMap[logId];
    delete this.inviteLogIdMap[logId];
    this.gc();
  };

  @action
  private removeLogInReferenceMap = (logId: LOG_ID) => {
    if (!this.referenceLogIdMap[logId]) return;
    const log = Object.assign({}, this.getInner(logId));
    log.flags = LOG_FLAGS.DELETED;
    channelLogData.setData(log.id, log);
  };

  public setVisibleProperty = (logList: Log[]) => {
    runInAction(() => {
      const { showUserMap, showSentAtMap, showDayDividerMap } = this.calcVisibleProperty(logList, {
        showUser: true,
        showSentAt: true,
        showDayDivider: true,
      });

      this.showUserMap = showUserMap;
      this.showSentAtMap = showSentAtMap;
      this.showDayDividerMap = showDayDividerMap;
    });
  };

  protected calcVisibleProperty = (
    logList: Log[],
    {
      showUser,
      showSentAt,
      showDayDivider,
      canOverlapLogs,
    }: {
      showUser?: boolean;
      showSentAt?: boolean;
      showDayDivider?: boolean;
      canOverlapLogs?: (prevLog: Log, log: Log) => boolean;
    }
  ) => {
    canOverlapLogs ||= canOverlap;
    const showUserMap = {};
    const showSentAtMap = {};
    const showDayDividerMap = {};

    logList.forEach((log, index) => {
      const nextLog = logList[index + 1];
      const prevLog = logList[index - 1];
      if (showUser) {
        showUserMap[log.id] = !canOverlapLogs(prevLog, log);
      }
      if (showSentAt) {
        showSentAtMap[log.id] = !canOverlapLogs(log, nextLog);
      }
      if (showDayDivider) {
        showDayDividerMap[log.id] = !prevLog?.sentAt || !isInTimeUnit('day', log.sentAt, prevLog.sentAt);
      }
    });
    return {
      showUserMap,
      showSentAtMap,
      showDayDividerMap,
    };
  };

  public updateThreadInfo = (threadChannel: ThreadChannel) => {
    if (mainDetailUiState.contentsChannelId === threadChannel.parentChannelId) {
      this.updateLog({
        id: threadChannel.parentChannelLogId,
        thread: {
          channelId: threadChannel.channelId,
          totalCount: threadChannel.totalCount,
          lastUpdatedAt: threadChannel.updatedAt,
        },
      });
    }
  };
}

export default ChannelLogsStore;

export const channelLogsStore = new ChannelLogsStore();
